// THE STALE-POLL CONTRACT. Every poll in this console runs through this hook.
//
// THE LAW: a number is NEVER blanked because a refresh failed. Stale-and-labelled beats empty.
// An operator who sees "5 unheld" that is four minutes old can still act; one who sees a
// spinner where a number was cannot, and cannot tell whether the number changed or the server
// died. So the hook keeps the last good value, records when it was good, and hands the caller
// a sentence to say about it.
import { useCallback, useEffect, useRef, useState } from 'react';

/** The default cadence. Three consecutive misses is what "stale" means, everywhere. */
export const POLL_MS = 30_000;
export const STALE_AFTER = 3;

export interface PollState<T> {
  /** The last GOOD value. Never cleared by a failure — that is the whole point. */
  data: T | null;
  /** True only before the first success. A refresh in flight is not "loading". */
  loading: boolean;
  /** The most recent failure's message, verbatim. Null once a poll succeeds again. */
  error: string | null;
  lastOkAt: number | null;
  stale: boolean;
  /** What the masthead prints. Null when everything is current — a healthy poll is silent. */
  notice: string | null;
  refresh: () => Promise<void>;
}

const hhmm = (ms: number): string => {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * Poll `fn` every `intervalMs`. `fn` MUST be stable (wrap it in useCallback) — its identity is
 * the dependency that restarts the poll, so an inline arrow restarts it on every render.
 */
export function usePoll<T>(fn: () => Promise<T>, intervalMs: number = POLL_MS): PollState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const alive = useRef(true);

  const run = useCallback(async () => {
    try {
      const next = await fn();
      if (!alive.current) return;
      setData(next);
      setError(null);
      setLastOkAt(Date.now());
    } catch (err) {
      if (!alive.current) return;
      // The value stays. Only the error line changes.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [fn]);

  useEffect(() => {
    alive.current = true;
    void run();
    const poll = setInterval(() => { void run(); }, intervalMs);
    // A second, cheap timer so `stale` becomes true on time even while every fetch is failing
    // (a failed poll never updates lastOkAt, so nothing else would re-render this).
    const tick = setInterval(() => setNow(Date.now()), 1_000);
    return () => { alive.current = false; clearInterval(poll); clearInterval(tick); };
  }, [run, intervalMs]);

  const stale = lastOkAt !== null && now - lastOkAt > STALE_AFTER * intervalMs;
  const notice = lastOkAt === null
    ? error
    : stale
      ? `data updated ${hhmm(lastOkAt)} · reconnecting`
      : null;

  return { data, loading, error, lastOkAt, stale, notice, refresh: run };
}
